import {
  ARIA_MODULE_ID,
  type RemoteMount,
  type RemoteMountProps,
  type MountedRemote,
  type RemoteConfig,
  type RouteClaim,
  type ShellRouter,
} from '@wandb/forge-shell-contract';
import {useEffect, useMemo, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {useAriaContext} from '../aria/AriaContext';
import {analyticsForAria} from '../analytics/segment';
import {loadRemoteModule} from '../remoteLoader';
import {
  activeRouteClaim,
  shellRouteClaimTable,
  type RemoteRouteClaims,
} from '../routeClaims';
import {remoteLocation, shellPath} from '../location';
import {currentProductForPath} from './forgeProducts';

/** Matches a URL with an origin (a scheme or `//host`) rather than a path. */
const HAS_ORIGIN = /^(?:[a-z][a-z0-9+.-]*:|\/\/)/i;

/**
 * `to` as a URL when it names an origin: with a scheme, protocol-relative, or
 * as this page's bare host (`forge.example/agent-lens`). Otherwise undefined.
 */
function urlOf(to: string): URL | undefined {
  const {host, href} = window.location;
  // Bare host first: `host:port/…` would otherwise read as a scheme.
  if (to.startsWith(host)) {
    return new URL(`//${to}`, href);
  }
  return HAS_ORIGIN.test(to) ? new URL(to, href) : undefined;
}

/**
 * Where ARIA's router sends `to`. ARIA links across products, so beyond the
 * W&B remote paths every router takes, an absolute URL on this origin is
 * routed as given. Scoped to ARIA for now; if other products want the same,
 * this belongs in the router `App.tsx` hands every remote, agreed with the
 * core UX team.
 */
function ariaShellPath(routeClaims: readonly RouteClaim[], to: string): string {
  const url = urlOf(to);
  return url?.origin === window.location.origin
    ? `${url.pathname}${url.search}${url.hash}`
    : shellPath(routeClaims, to);
}

function createRouter(
  remote: RemoteRouteClaims,
  basePath: string,
  activeProductId: string | undefined,
  pageRouteClaim:
    | NonNullable<ReturnType<typeof activeRouteClaim>>['routeClaim']
    | undefined,
  location: ReturnType<typeof useLocation>,
  navigate: ReturnType<typeof useNavigate>
): ShellRouter {
  return {
    basePath,
    activeProductId,
    routeClaims: remote.routeClaims,
    location: pageRouteClaim
      ? remoteLocation(
          pageRouteClaim,
          location.pathname,
          location.search,
          location.hash,
          location.state
        )
      : {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          state: location.state,
        },
    createHref(to) {
      return shellPath(remote.routeClaims, to);
    },
    navigate(to, options) {
      navigate(ariaShellPath(remote.routeClaims, to), {
        replace: options?.replace,
        state: options?.state,
      });
    },
    go(delta) {
      window.history.go(delta);
    },
  };
}

export const AriaNavHost = ({
  remote,
  selectedAccount,
}: {
  remote: RemoteConfig;
  selectedAccount: RemoteMountProps['selectedAccount'];
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const routeTable = shellRouteClaimTable();
  const activeRoute = activeRouteClaim(routeTable, location.pathname);
  const pageRouteClaim = activeRoute?.routeClaim;
  const activeProductId = currentProductForPath(
    location.pathname,
    routeTable
  )?.id;
  const remoteClaims = routeTable.find(
    remoteClaims => remoteClaims.name === 'wandb'
  );
  const basePath = remoteClaims?.basePath;
  const router = useMemo(
    () =>
      remoteClaims && basePath
        ? createRouter(
            remoteClaims,
            basePath,
            activeProductId,
            pageRouteClaim,
            location,
            navigate
          )
        : null,
    [
      activeProductId,
      basePath,
      location,
      navigate,
      pageRouteClaim,
      remoteClaims,
    ]
  );
  return router ? (
    <AriaNavHostMounted
      remote={remote}
      router={router}
      selectedAccount={selectedAccount}
      showToastRegion={activeRoute?.remote.name !== 'wandb'}
    />
  ) : null;
};

const AriaNavHostMounted = ({
  remote,
  router,
  selectedAccount,
  showToastRegion,
}: {
  remote: RemoteConfig;
  router: ShellRouter;
  selectedAccount: RemoteMountProps['selectedAccount'];
  showToastRegion: boolean;
}) => {
  const aria = useAriaContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const props = useMemo<RemoteMountProps>(
    () => ({
      router,
      selectedAccount,
      aria,
      analytics: analyticsForAria,
      assetBase: remote.assetBase,
      showToastRegion,
      onError(error) {
        if (error.kind === 'unknown') {
          console.error(`ARIA remote error: ${error.message ?? error.kind}`);
        }
      },
    }),
    [remote.assetBase, router, aria, selectedAccount, showToastRegion]
  );
  const latestPropsRef = useRef(props);
  const mountedRef = useRef<MountedRemote | undefined>(undefined);

  useEffect(() => {
    latestPropsRef.current = props;
    mountedRef.current?.update?.(props);
  }, [props]);

  useEffect(() => {
    let active = true;

    void loadRemoteModule<RemoteMount>(remote, ARIA_MODULE_ID)
      .then(mount => {
        if (!active || !containerRef.current) {
          return;
        }
        mountedRef.current = mount(
          containerRef.current,
          latestPropsRef.current
        );
        mountedRef.current.update?.(latestPropsRef.current);
      })
      .catch(error => {
        if (active) {
          console.error('ARIA remote failed to load', error);
        }
      });

    return () => {
      active = false;
      mountedRef.current?.unmount();
      mountedRef.current = undefined;
    };
  }, [remote]);

  return <div ref={containerRef} />;
};
